import { type Dictionary, type EntityState } from '@ngrx/entity';
import { type Draft } from 'immer';
import { omit } from 'lodash-es';

import { type EngineStateBase, navMenuItemsAdapter } from '@blaze-runtime/engine-data-access';
import { type CanvasElementFrom, type CanvasElementId } from '@blaze-runtime/engine-domain';
import { NavMenu } from '@blaze-shared/elements-domain';
import { type NamespaceId } from '@blaze-shared/workflow-domain';

import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import {
  areSectionsPresent,
  DEFAULT_ITEM_LABEL,
  DEFAULT_SUBMENU_ITEM_LABEL,
  generateNewItemReference,
  generateNewSectionItem,
  generateNewSharedItem,
  isLegacyNavMenuSharedItem,
  isNavMenuItemReference,
  isNavMenuSection,
} from '../../services/nav-menu-items.service';

/**
 * Flattens the structure of shared nav menu items.
 * It is easier to access individual items on a flattened structure than with a nested structure.
 * @param items
 */
export function toSharedMenuItems(
  items: (NavMenu.LegacySharedItem | NavMenu.SharedItem)[],
): NavMenu.SharedItem[] {
  return items.reduce<NavMenu.SharedItem[]>((acc, item) => {
    if (!isLegacyNavMenuSharedItem(item)) {
      acc.push(item);
      return acc;
    }

    const childrenIds: string[] = [];
    for (const child of item.props?.items ?? []) {
      acc.push({
        ...child,
        parentId: item.id,
        props: omit(child.props, ['items']),
      });
      childrenIds.push(child.id);
    }

    acc.push({
      ...item,
      children: childrenIds,
      props: omit(item.props, ['items']),
    });

    return acc;
  }, []);
}

/**
 * Converts to a nested structure of shared nav menu items.
 * Changing anything in the layout will immediately trigger sync.
 * For now, we want to keep the data format unchanged to
 * avoid potential issues with db rollbacks.
 * @param state
 */
export function toLegacySharedMenuItems(
  state: EntityState<NavMenu.SharedItem>,
): NavMenu.LegacySharedItem[] {
  return state.ids.reduce<NavMenu.LegacySharedItem[]>((acc, itemId) => {
    const stateItem = state.entities[itemId];

    if (!stateItem || stateItem.parentId) {
      return acc;
    }

    const newItem: NavMenu.LegacySharedItem = {
      id: stateItem.id,
      ns: stateItem.ns,
      props: {
        ...stateItem.props,
      },
    };
    if ('isPristine' in stateItem) {
      newItem.isPristine = stateItem.isPristine;
    }
    if (stateItem.children) {
      const items: NavMenu.LegacySharedSubmenuItem[] = [];
      for (const childId of stateItem.children) {
        const child = state.entities[childId];
        if (!child) {
          continue;
        }
        const childItem: NavMenu.LegacySharedItem = {
          id: child.id,
          ns: child.ns,
          props: child.props,
        };
        if ('isPristine' in child) {
          childItem.isPristine = child.isPristine;
        }
        items.push(childItem);
      }

      (newItem.props as NavMenu.LegacySharedItemProps).items = items;
    }
    acc.push(newItem);
    return acc;
  }, []);
}

export function sanitizeNavMenusAfterItemDeleteMutably(draft: Draft<EngineStateBase>): void {
  draft.ids.forEach((elementId) => {
    const element = draft.entities[elementId];
    if (!isElementType(element, 'navMenu')) {
      return;
    }

    const storage = element.props?.content.storage;
    if (!storage) {
      return;
    }

    const items = Object.values(storage);
    for (const item of items) {
      if (!isNavMenuItemReference(item)) {
        continue;
      }

      const sharedItem = draft.navMenu.entities[item.targetId];
      if (!sharedItem) {
        removeFromLocalStorageMutably(storage, item.id);
      }
    }
  });
}

export function sanitizeNavMenusAfterElementRemoveMutably(
  draft: Draft<EngineStateBase>,
  element: CanvasElementFrom<'navMenu'>,
): void {
  const storage = element.props?.content.storage;
  if (!storage) {
    return;
  }

  const storageArray = Object.values(storage);
  for (const item of storageArray) {
    if (!isNavMenuItemReference(item)) {
      continue;
    }

    const globalItem = draft.navMenu.entities[item.targetId];
    if (globalItem?.isPristine) {
      deleteSharedMenuItemMutably(draft, globalItem.id);
    }
  }
}

export function updateNavMenuDirectionBasedOnSectionsMutably(
  element: Draft<CanvasElementFrom<'navMenu'>>,
): void {
  const hasSections = areSectionsPresent(element.props?.content?.storage);
  if (hasSections) {
    element.props.content.direction = 'vertical';
  }
}

/**
 * Removes an item and its children from shared storage.
 * @param draft
 * @param itemId
 */
export function deleteSharedMenuItemMutably(draft: Draft<EngineStateBase>, itemId: string): void {
  const item = draft.navMenu.entities[itemId];

  if (!item) {
    return;
  }

  if (item.children) {
    for (const childId of item.children) {
      deleteSharedMenuItemMutably(draft, childId);
    }
  }

  draft.navMenu = navMenuItemsAdapter.removeOne(itemId, draft.navMenu);
}

export function removeFromLocalStorageMutably(
  storage: NavMenu.LocalStorage,
  idToRemove: string,
): void {
  const storageItems = Object.values(storage);
  const idsToRemove = getAllChildrenIds(idToRemove, storage);

  for (const storageItem of storageItems) {
    if (!storageItem.children) {
      continue;
    }

    storageItem.children = storageItem.children.filter((childId) => !idsToRemove.includes(childId));
  }

  for (const id of idsToRemove) {
    delete storage[id];
  }
}

export function removeSectionMutably(storage: Draft<NavMenu.LocalStorage>, itemId: string): void {
  const item = storage[itemId];
  if (!isNavMenuSection(item)) {
    console.warn('Target item is not a section.');
    return;
  }

  const parent = storage[item.parentId ?? NavMenu.ROOT_LOCAL_ITEM_ID];
  const childrenCopy = [...(item.children ?? [])];

  item.children = [];
  parent.children ??= [];
  parent.children.splice(findIndexForNewMenuItem(itemId, parent.children), 0, ...childrenCopy);

  for (const childId of childrenCopy) {
    const childItem = storage[childId];
    if (!childItem) {
      continue;
    }
    childItem.parentId = parent.id === NavMenu.ROOT_LOCAL_ITEM_ID ? undefined : parent.id;
  }

  removeFromLocalStorageMutably(storage, itemId);
}

/**
 * Generate initial items. Mutates local storage.
 * @param storage
 * @param initItems
 * @param namespaceId
 */
export function generateInitialItemsMutably(
  storage: NavMenu.LocalStorage,
  initItems: NavMenu.InitItem[],
  namespaceId: NamespaceId,
): NavMenu.SharedItem[] {
  let sharedResult: NavMenu.SharedItem[] = [];

  for (const item of initItems) {
    if (item.type === 'section') {
      const { sectionItem, localItems, sharedItems } = generateInitSection(item, namespaceId);

      storage[sectionItem.id] = sectionItem;
      storage[NavMenu.ROOT_LOCAL_ITEM_ID].children.push(sectionItem.id);
      for (const localItem of localItems) {
        storage[localItem.id] = localItem;
      }
      sharedResult = sharedResult.concat(sharedItems);
    } else {
      const { referenceItem, sharedItems } = generateInitShared(item, namespaceId);
      storage[referenceItem.id] = referenceItem;
      storage[NavMenu.ROOT_LOCAL_ITEM_ID].children.push(referenceItem.id);
      sharedResult = sharedResult.concat(sharedItems);
    }
  }

  return sharedResult;
}

function generateInitSection(
  item: NavMenu.InitItem,
  namespaceId: NamespaceId,
): {
  sectionItem: NavMenu.SectionItem;
  localItems: NavMenu.MenuItem[];
  sharedItems: NavMenu.SharedItem[];
} {
  const localResult: NavMenu.MenuItem[] = [];
  let sharedResult: NavMenu.SharedItem[] = [];

  const newSection = generateNewSectionItem(item.props);

  if (item.children) {
    for (const child of item.children) {
      // Nesting sections in sections is not allowed.
      if (child.type !== 'shared') {
        continue;
      }

      const { referenceItem, sharedItems } = generateInitShared(child, namespaceId);
      newSection.children ??= [];
      newSection.children.push(referenceItem.id);
      referenceItem.parentId = newSection.id;
      localResult.push(referenceItem);
      sharedResult = sharedResult.concat(sharedItems);
    }
  }

  return { sectionItem: newSection, localItems: localResult, sharedItems: sharedResult };
}

function generateInitShared(
  item: NavMenu.InitItem,
  namespaceId: NamespaceId,
): {
  referenceItem: NavMenu.ReferenceItem;
  sharedItems: NavMenu.SharedItem[];
} {
  let sharedResult: NavMenu.SharedItem[] = [];

  const newSharedItem = generateNewSharedItem(namespaceId, item.props);
  const newReferenceItem = generateNewItemReference(newSharedItem.id);

  if (item.children) {
    sharedResult = sharedResult.concat(
      generateInitSharedChildren(item.children, newSharedItem, namespaceId),
    );
  }

  sharedResult.push(newSharedItem);

  return {
    referenceItem: newReferenceItem,
    sharedItems: sharedResult,
  };
}

function generateInitSharedChildren(
  children: NavMenu.InitItem[],
  parentSharedItem: NavMenu.SharedItem,
  namespaceId: NamespaceId,
): NavMenu.SharedItem[] {
  const sharedResult: NavMenu.SharedItem[] = [];

  for (const item of children) {
    const newSharedItem = generateNewSharedItem(namespaceId, item.props);
    parentSharedItem.children ??= [];
    parentSharedItem.children.push(newSharedItem.id);
    newSharedItem.parentId = parentSharedItem.id;
    sharedResult.push(newSharedItem);

    if (item.children) {
      generateInitSharedChildren(item.children, newSharedItem, namespaceId);
    }
  }

  return sharedResult;
}

function findIndexForNewMenuItem(
  targetId?: string,
  children?: string[],
  direction: NavMenu.DropDirection = 'before',
): number {
  if (!children?.length) {
    return 0;
  }

  if (direction === 'inside') {
    return children.length;
  }

  if (!targetId) {
    return direction === 'before' ? 0 : children.length;
  }

  let index = children.indexOf(targetId);
  if (index === -1) {
    index = direction === 'before' ? 0 : children.length;
  } else {
    index = direction === 'before' ? index : index + 1;
  }
  return Math.max(0, Math.min(index, children.length));
}

export function addLocalMenuItemMutably(
  draft: Draft<EngineStateBase>,
  placement: NavMenu.Placement,
  itemType: NavMenu.ItemType,
): void {
  const element = draft.entities[placement.elementId];
  if (!isElementType(element, 'navMenu')) {
    return;
  }
  const storage = element.props?.content.storage;
  if (!storage) {
    return;
  }

  let newItem: NavMenu.MenuItem;
  if (itemType === 'section') {
    newItem = generateNewSectionItem();
  } else if (itemType === 'reference' && placement.targetItemId) {
    newItem = generateNewItemReference(placement.targetItemId);
  } else {
    return;
  }

  const parentItem = storage[placement.parentLocalItemId ?? NavMenu.ROOT_LOCAL_ITEM_ID];
  if (!parentItem) {
    return;
  }
  storage[newItem.id] = newItem;
  if (placement.parentLocalItemId) {
    newItem.parentId = placement.parentLocalItemId;
  }
  parentItem.children = parentItem.children ?? [];

  const targetIndex = findIndexForNewMenuItem(
    placement.targetItemId,
    parentItem.children,
    placement.direction,
  );
  parentItem.children.splice(targetIndex, 0, newItem.id);

  updateNavMenuDirectionBasedOnSectionsMutably(element);
}

export function addSharedMenuItemMutably(
  draft: Draft<EngineStateBase>,
  namespaceId: string,
  placement: NavMenu.Placement,
  initProps?: Partial<NavMenu.ItemProps>,
): void {
  const element = draft.entities[placement.elementId];
  if (!isElementType(element, 'navMenu')) {
    return;
  }

  const localStorage = element.props?.content.storage;
  if (!localStorage) {
    throw new Error('Nav menu element should have storage initialized.');
  }

  const label = placement.parentSharedItemId ? DEFAULT_SUBMENU_ITEM_LABEL : DEFAULT_ITEM_LABEL;
  const newItem = generateNewSharedItem(namespaceId, { label, ...initProps });
  draft.navMenu = navMenuItemsAdapter.addOne(newItem, draft.navMenu);
  const sharedStorage = draft.navMenu.entities;

  // Add a shared submenu item
  if (placement.parentSharedItemId) {
    const parentItem = sharedStorage[placement.parentSharedItemId];
    if (!parentItem) {
      throw new Error('Could not find parent item in shared storage.');
    }

    const targetIndex = findIndexForNewMenuItem(
      placement.targetItemId,
      parentItem.children,
      placement.direction,
    );
    parentItem.children = parentItem.children ?? [];
    parentItem.children.splice(targetIndex, 0, newItem.id);
    delete parentItem.isPristine;
    newItem.parentId = parentItem.id;
    draft.navMenu.lastAdded = { itemId: parentItem.id, subItemId: newItem.id };
    return;
  }

  // Add a shared menu item and reference it in the element
  const newRef = generateNewItemReference(newItem.id);
  const parentItem = localStorage[placement.parentLocalItemId ?? NavMenu.ROOT_LOCAL_ITEM_ID];
  const targetIndex = findIndexForNewMenuItem(
    placement.targetItemId,
    parentItem.children,
    placement.direction,
  );
  parentItem.children = parentItem.children ?? [];
  parentItem.children.splice(targetIndex, 0, newRef.id);
  if (placement.parentLocalItemId) {
    newRef.parentId = placement.parentLocalItemId;
  }
  localStorage[newRef.id] = newRef;
  draft.navMenu.lastAdded = { itemId: newRef.id };
}

/**
 * Moves items in local / shared storage.
 * TODO: [MG] This method is highly unoptimized and contains lots of repetitions.
 *            MAKE IT BETTER!
 * @param draft
 * @param elementId
 * @param dragRef
 * @param targetRef
 * @param direction
 */
export function moveItemMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  dragRef: NavMenu.RefIds,
  targetRef: NavMenu.RefIds,
  direction: NavMenu.DropDirection,
): void {
  const storagePageId = draft.pages.find((page) => page.pageType === 'NavMenus')?.id;
  if (!storagePageId) {
    return;
  }

  const element = draft.entities[elementId];
  if (!isElementType(element, 'navMenu')) {
    return;
  }

  const localStorage = element.props.content.storage;
  if (!localStorage) {
    throw new Error('Nav menu element should have storage initialized.');
  }

  let sharedStorage = draft.navMenu.entities;
  const drag = getItemFromRefs(dragRef, localStorage, sharedStorage);
  const target = getItemFromRefs(targetRef, localStorage, sharedStorage);

  // Local to local item
  if (drag.l && target.l && !(direction === 'inside' && target.l?.type === 'reference')) {
    const maxDepth = 3;
    const targetLChain = getChain(target.l, localStorage);
    const isTargetInSection = targetLChain.some((item) => item.type === 'section');

    if (drag.l.type === 'section' && isTargetInSection && direction === 'inside') {
      console.warn('Cannot move item in section to another section.');
      return;
    }

    let depth = targetLChain.length;
    if (isTargetInSection) {
      depth = depth - 1;
    }
    if (direction === 'inside') {
      depth = depth + 1;
    }

    let idsToMove: string[];
    if (depth < maxDepth) {
      idsToMove = [drag.l.id];
    } else {
      idsToMove = getAllChildrenIds(drag.l.id, localStorage);
    }

    let parentItem = direction === 'inside' ? target.l : target.lp;
    if (depth > maxDepth) {
      parentItem = target.lp;
    }

    if (!parentItem) {
      return;
    }
    parentItem.children ??= [];

    if (drag.lp?.children) {
      drag.lp.children = drag.lp.children.filter((childId) => childId !== drag.l?.id);
    }

    parentItem.children.splice(
      findIndexForNewMenuItem(target.l.id, parentItem.children, direction),
      0,
      ...idsToMove,
    );

    for (const id of idsToMove) {
      const item = localStorage[id];
      if (!item) {
        continue;
      }
      item.parentId = parentItem.id === NavMenu.ROOT_LOCAL_ITEM_ID ? undefined : parentItem.id;
      if (depth >= maxDepth) {
        delete item.children;
      }
    }

    return;
  }

  // Shared to shared item
  if (drag.s && target.s) {
    const maxDepth = 2;
    const targetSChain = getChain(target.s, sharedStorage);

    if (drag.l && drag.lp?.children) {
      drag.lp.children = drag.lp.children.filter((childId) => childId !== drag.l?.id);
      delete localStorage[drag.l.id];
    }

    let depth = targetSChain.length;
    if (direction === 'inside') {
      depth = depth + 1;
    }

    let idsToMove: string[];
    if (depth < maxDepth) {
      idsToMove = [drag.s.id];
    } else {
      idsToMove = getAllChildrenIds(drag.s.id, sharedStorage);
    }

    let parentItem: NavMenu.MenuItem | NavMenu.SharedItem | undefined =
      direction === 'inside' ? target.s : target.sp;
    let newReference: NavMenu.ReferenceItem | undefined;
    if (!parentItem && target.lp && !drag.l && direction !== 'inside') {
      parentItem = target.lp;
      newReference = generateNewItemReference(drag.s.id);
      newReference.parentId =
        parentItem.id === NavMenu.ROOT_LOCAL_ITEM_ID ? undefined : parentItem.id;
      localStorage[newReference.id] = newReference;
      idsToMove = [newReference.id];
      delete drag.s.parentId;
    }

    if (!parentItem) {
      return;
    }
    parentItem.children ??= [];

    if (drag.sp?.children) {
      drag.sp.children = drag.sp.children.filter((childId) => childId !== drag.s?.id);
    }

    const idToFind = newReference && target.l?.id ? target.l.id : target.s.id;
    parentItem.children.splice(
      findIndexForNewMenuItem(idToFind, parentItem.children, direction),
      0,
      ...idsToMove,
    );

    for (const id of idsToMove) {
      const item = sharedStorage[id];
      if (!item) {
        continue;
      }
      item.parentId = parentItem.id === NavMenu.ROOT_LOCAL_ITEM_ID ? undefined : parentItem.id;
      if (depth >= maxDepth) {
        delete item.children;
      }
    }

    return;
  }

  // Shared to local item
  if (drag.s && target.l) {
    const maxDepth = 3;
    const targetLChain = getChain(target.l, localStorage);

    let depth = targetLChain.length;
    if (direction === 'inside') {
      depth = depth + 1;
    }

    let sharedIdsToMove: string[];
    if (depth < maxDepth) {
      sharedIdsToMove = [drag.s.id];
    } else {
      sharedIdsToMove = getAllChildrenIds(drag.s.id, sharedStorage);
    }

    let parentItem = direction === 'inside' ? target.l : target.lp;
    if (depth > maxDepth) {
      parentItem = target.lp;
    }
    if (!parentItem) {
      return;
    }
    parentItem.children ??= [];

    if (drag.sp?.children) {
      drag.sp.children = drag.sp.children.filter((childId) => childId !== drag.s?.id);
    }

    const newReferences = sharedIdsToMove.map((id) => generateNewItemReference(id));

    for (const ref of newReferences) {
      localStorage[ref.id] = ref;
      ref.parentId = parentItem.id === NavMenu.ROOT_LOCAL_ITEM_ID ? undefined : parentItem.id;
    }

    parentItem.children.splice(
      findIndexForNewMenuItem(target.l.id, parentItem.children, direction),
      0,
      ...newReferences.map((ref) => ref.id),
    );

    for (const id of sharedIdsToMove) {
      const item = sharedStorage[id];
      if (!item) {
        continue;
      }
      item.parentId = undefined;
      if (depth >= maxDepth) {
        delete item.children;
      }
    }

    return;
  }

  // Local to shared item
  if (drag.l && target.s) {
    const maxDepth = 2;
    const targetSChain = getChain(target.s, sharedStorage);

    let depth = targetSChain.length;
    if (direction === 'inside') {
      depth = depth + 1;
    }

    const localIdsToMove = getAllChildrenIds(drag.l.id, localStorage);
    let parentItem = direction === 'inside' ? target.s : target.sp;
    if (depth > maxDepth) {
      parentItem = target.sp;
    }
    if (!parentItem) {
      return;
    }
    parentItem.children ??= [];

    const conversionResult = convertLocalToSharedMutably(
      drag.l,
      storagePageId,
      localStorage,
      sharedStorage,
    );
    if (!conversionResult?.startId || !conversionResult?.newSharedStorage) {
      return;
    }
    const newSharedItems = Object.values(conversionResult.newSharedStorage).filter(
      (item) => !!item,
    );
    const startItem = conversionResult.newSharedStorage[conversionResult.startId];
    if (!startItem) {
      return;
    }

    draft.navMenu = navMenuItemsAdapter.addMany(newSharedItems, draft.navMenu);
    sharedStorage = draft.navMenu.entities;

    let sharedIdsToMove: string[];
    if (depth < maxDepth) {
      sharedIdsToMove = [startItem.id];
    } else {
      sharedIdsToMove = getAllChildrenIds(startItem.id, sharedStorage);
    }

    parentItem.children.splice(
      findIndexForNewMenuItem(target.s.id, parentItem.children, direction),
      0,
      ...sharedIdsToMove,
    );

    if (drag.lp?.children) {
      drag.lp.children = drag.lp.children.filter((childId) => childId !== drag.l?.id);
    }

    // Remove any local ids
    for (const id of localIdsToMove) {
      delete localStorage[id];
    }

    for (const id of sharedIdsToMove) {
      const item = sharedStorage[id];
      if (!item) {
        continue;
      }
      item.parentId = parentItem.id;
      if (depth >= maxDepth) {
        delete item.children;
      }
    }
  }
}

function getItemFromRefs(
  refIds: NavMenu.RefIds,
  localStorage: NavMenu.LocalStorage,
  sharedStorage: NavMenu.SharedStorage,
): {
  l: NavMenu.MenuItem | undefined;
  lp: NavMenu.MenuItem | undefined;
  s: NavMenu.SharedItem | undefined;
  sp: NavMenu.SharedItem | undefined;
} {
  return {
    l: localStorage[refIds.localId ?? ''],
    lp: localStorage[refIds.parentLocalId ?? NavMenu.ROOT_LOCAL_ITEM_ID],
    s: sharedStorage[refIds.sharedId ?? ''],
    sp: sharedStorage[refIds.parentSharedId ?? ''],
  };
}

export function getAllChildrenIds(
  itemId: string,
  storage: Dictionary<{ id: string; children?: string[] }>,
): string[] {
  const item = storage[itemId];

  if (!item) {
    return [];
  }

  let result = [itemId];
  for (const childId of item.children ?? []) {
    result = result.concat(getAllChildrenIds(childId, storage));
  }

  return result;
}

export function walkThroughParents<T extends NavMenu.StorageItem>(
  id: string,
  storage: NavMenu.Storage<T>,
  callbackFn: (item: T, stop: () => void) => void,
): void {
  let isStopped = false;

  const stop = (): void => {
    isStopped = false;
  };

  const walkRecursive = (currentId: string): void => {
    const item = storage[currentId];
    if (!item) {
      return;
    }
    callbackFn(item, stop);
    if (isStopped || currentId === NavMenu.ROOT_LOCAL_ITEM_ID) {
      return;
    }
    walkRecursive(item.parentId ?? NavMenu.ROOT_LOCAL_ITEM_ID);
  };

  walkRecursive(id);
}

export function getChain<T extends NavMenu.StorageItem = NavMenu.StorageItem>(
  item: T,
  storage: Dictionary<T>,
): T[] {
  const result: T[] = [];
  walkThroughParents(item.id, storage, (item) => {
    result.push(item);
  });
  return result;
}

export function convertLocalToSharedMutably(
  localItem: NavMenu.MenuItem,
  namespaceId: NamespaceId,
  localStorage: NavMenu.LocalStorage,
  sharedStorage: NavMenu.SharedStorage,
  resultStorage?: NavMenu.SharedStorage,
): { startId: string | undefined; newSharedStorage: NavMenu.SharedStorage } | undefined {
  if (localItem.type === 'reference') {
    return { startId: localItem.targetId, newSharedStorage: sharedStorage };
  }

  if (localItem.type === 'section') {
    return undefined;
  }

  resultStorage ??= {};
  const newShared = generateNewSharedItem(namespaceId, localItem.props, false);
  resultStorage[newShared.id] = newShared;
  if (localItem.children?.length) {
    for (const childId of localItem.children) {
      const child = localStorage[childId];
      if (!child) {
        continue;
      }

      const newSharedChild = convertLocalToSharedMutably(
        child,
        namespaceId,
        localStorage,
        sharedStorage,
        resultStorage,
      );
      if (!newSharedChild?.startId) {
        continue;
      }
      newShared.children ??= [];
      newShared.children.push(newSharedChild.startId);
    }
  }

  return { startId: newShared.id, newSharedStorage: resultStorage };
}
